import type { PostDetail, TradeStatus } from '@tomato/shared';
import {
  AFFILIATION_LABELS,
  BOARD_LABELS,
  formatPrice,
  isMarketBoard,
  TALK_CATEGORY_LABELS,
  tradeStatusLabel,
  TRADE_PLACE_LABELS,
  TRADE_STATUSES,
  TRADE_TYPE_LABELS,
} from '@tomato/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import { CommentSection } from '../components/CommentSection';
import { ImageViewer } from '../components/ImageViewer';
import { Badge, Button, Notice, PostDetailSkeleton, Select } from '../components/ui';
import { formatWhen, renderMarkdown } from '../lib/markdown';
import { useRefreshStore } from '../store/refresh';
import { useSessionStore } from '../store/session';

export function PostDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const myId = useSessionStore((s) => s.me?.id);
  const myRef = useSessionStore((s) => s.me?.referralCode);

  const refreshTick = useRefreshStore((s) => s.tick);
  const [post, setPost] = useState<PostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  /** 풀스크린 사진 뷰어 — 열려 있으면 시작 인덱스, 닫혀 있으면 null. */
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  /**
   * 공유 — 글 링크를 복사한다. 초대코드(?ref=)를 붙여, 이 링크로 가입하면
   * 공유한 사람이 추천인이 된다(공유 = 초대). 링크는 로그아웃 방문자에게 프리뷰로 열린다.
   */
  async function sharePost() {
    if (!post) return;
    const ref = myRef ? `?ref=${myRef}` : '';
    const url = `${window.location.origin}/posts/${post.id}${ref}`;
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: post.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1600);
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1600);
      } catch {
        /* 복사 실패는 조용히 */
      }
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void api
      .get<{ post: PostDetail }>(`/api/posts/${id}`)
      .then((res) => !cancelled && setPost(res.post))
      .catch(() => !cancelled && setError('글을 찾을 수 없어요.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, refreshTick]); // 당겨서 새로고침 포함

  const html = useMemo(() => (post ? renderMarkdown(post.bodyMd) : ''), [post]);

  async function changeStatus(next: TradeStatus) {
    if (!post || busy) return;
    setBusy(true);
    try {
      const res = await api.patch<{ post: PostDetail }>(`/api/posts/${post.id}/status`, {
        tradeStatus: next,
      });
      setPost(res.post);
    } catch {
      setError('상태를 바꾸지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleLike() {
    if (!post || busy) return;
    setBusy(true);
    try {
      const res = post.liked
        ? await api.del<{ liked: boolean; likeCount: number }>(`/api/posts/${post.id}/like`)
        : await api.post<{ liked: boolean; likeCount: number }>(`/api/posts/${post.id}/like`);
      setPost({ ...post, liked: res.liked, likeCount: res.likeCount });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  async function startChat() {
    if (!post || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ roomId: string }>(`/api/posts/${post.id}/chat`);
      navigate(`/chats/${res.roomId}`);
    } catch (err) {
      // 찜 게이트(D1): 찜하지 않았으면 서버가 막는다. 화면에서 찜을 먼저 유도한다.
      if (err instanceof ApiError && err.code === 'favorite_required') {
        setError('먼저 ❤️ 찜하면 판매자와 대화할 수 있어요.');
      } else {
        setError(err instanceof ApiError ? err.message : '대화를 열지 못했어요.');
      }
      setBusy(false);
    }
  }

  async function toggleFavorite() {
    if (!post || busy) return;
    setBusy(true);
    try {
      const res = post.favorited
        ? await api.del<{ favorited: boolean; favoriteCount: number }>(
            `/api/posts/${post.id}/favorite`,
          )
        : await api.put<{ favorited: boolean; favoriteCount: number }>(
            `/api/posts/${post.id}/favorite`,
          );
      setPost({ ...post, favorited: res.favorited, favoriteCount: res.favoriteCount });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  async function bump() {
    if (!post || busy) return;
    setBusy(true);
    try {
      await api.post(`/api/posts/${post.id}/bump`);
      const res = await api.get<{ post: PostDetail }>(`/api/posts/${post.id}`);
      setPost(res.post);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '끌올하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  async function refreshCounts() {
    if (!post) return;
    try {
      const res = await api.get<{ post: PostDetail }>(`/api/posts/${post.id}`);
      setPost(res.post);
    } catch {
      /* 카운트 갱신 실패는 조용히 넘긴다 */
    }
  }

  async function remove() {
    if (!post || busy) return;
    setBusy(true);
    try {
      await api.del(`/api/posts/${post.id}`);
      navigate(`/${post.boardSlug}`, { replace: true });
    } catch {
      setError('삭제하지 못했어요.');
      setBusy(false);
    }
  }

  if (loading) {
    return <PostDetailSkeleton />;
  }

  if (!post) {
    return (
      <div className="py-24 text-center">
        <p className="text-[15px] text-ink-500">{error || '글을 찾을 수 없어요.'}</p>
        <Button variant="secondary" className="mt-5" onClick={() => navigate(-1)}>
          돌아가기
        </Button>
      </div>
    );
  }

  return (
    <article className="py-6">
      <Link
        viewTransition
        to={`/${post.boardSlug}`}
        className="text-[13px] text-ink-400 transition hover:text-ink-700"
      >
        ← {BOARD_LABELS[post.boardSlug]}
      </Link>

      <header className="mt-4">
        <div className="mb-2 flex items-center gap-1.5">
          {post.category && <Badge tone="accent">{TALK_CATEGORY_LABELS[post.category]}</Badge>}
          {post.tradeType && <Badge tone="accent">{TRADE_TYPE_LABELS[post.tradeType]}</Badge>}
          {post.tradeStatus && post.tradeStatus !== 'selling' && (
            <Badge>{tradeStatusLabel(post.tradeStatus, post.tradeType)}</Badge>
          )}
        </div>
        <h1
          className={`text-[24px] leading-snug font-semibold tracking-tight text-ink-900 ${
            post.tradeStatus === 'sold' ? 'text-ink-400 line-through' : ''
          }`}
        >
          {post.title}
        </h1>
        {formatPrice(post.price, post.tradeType) && (
          <p className="mt-1.5 text-[17px] font-semibold text-ink-900">
            {formatPrice(post.price, post.tradeType)}
          </p>
        )}
        {post.tradePlace && (
          <p className="mt-1 text-[13px] text-ink-500">📍 {TRADE_PLACE_LABELS[post.tradePlace]}</p>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-[13px] text-ink-400">
          <span className="font-medium text-ink-700">{post.author.nickname}</span>
          {/* 비공개면 서버가 키 자체를 안 보낸다. 프론트에서 숨기는 게 아니다. */}
          {post.author.affiliation && (
            <>
              <span aria-hidden>·</span>
              <span>{AFFILIATION_LABELS[post.author.affiliation]}</span>
            </>
          )}
          <span aria-hidden>·</span>
          <span>{formatWhen(post.createdAt)}</span>
          <span aria-hidden>·</span>
          <span>조회 {post.viewCount}</span>
        </div>
      </header>

      {/* 거래 상태는 작성자만, 그리고 상세에서 한 번에 바꿀 수 있어야 한다. */}
      {post.canEdit && post.tradeType && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-surface p-3 shadow-card">
          <span className="shrink-0 pl-1 text-[13px] whitespace-nowrap text-ink-500">거래 상태</span>
          <Select
            className="ml-auto w-32 py-2 text-[14px]"
            value={post.tradeStatus ?? 'selling'}
            disabled={busy}
            onChange={(e) => void changeStatus(e.target.value as TradeStatus)}
          >
            {TRADE_STATUSES.map((code) => (
              <option key={code} value={code}>
                {tradeStatusLabel(code, post.tradeType)}
              </option>
            ))}
          </Select>
        </div>
      )}

      {post.images.length > 0 && (
        <div className="mt-6 space-y-2">
          {post.images.map((img, i) => (
            /**
             * loading="lazy"를 쓰지 않는다. 크기를 모르는 이미지는 로드 전 높이가 0이라
             * 전부 같은 자리에 겹쳐 쌓이고, 그러면 브라우저가 "화면에 보인다"고 판정하지 못해
             * 영영 로드되지 않는다. 글당 최대 5장이라 즉시 받아도 부담이 없다.
             * (목록 썸네일은 size-16으로 크기가 고정돼 있어 lazy가 안전하다.)
             */
            <button
              key={img.id}
              type="button"
              aria-label={`사진 ${i + 1} 크게 보기`}
              onClick={() => setViewerIndex(i)}
              className="block w-full cursor-zoom-in"
            >
              <img
                src={img.url}
                alt={`${post.title} 사진 ${i + 1}`}
                className="w-full rounded-2xl bg-ink-100 shadow-card"
              />
            </button>
          ))}
        </div>
      )}

      {/* 사진 탭 → 풀스크린 뷰어(스와이프·핀치 줌) */}
      {viewerIndex !== null && (
        <ImageViewer
          images={post.images}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}

      <div className="mt-6 rounded-2xl bg-surface p-6 hairline-accent">
        <div className="prose-tomato" dangerouslySetInnerHTML={{ __html: html }} />
      </div>

      {/*
        추천·찜은 자기 글에 못 한다(서버가 최종 판정). 버튼 자체를 감춘다.
        canEdit은 관리자에게도 true라 판정 기준으로 쓰면 관리자가 남의 글을 추천할 수 없다.
        찜은 장터 글에서만 의미가 있어(관심 상품 추적) market에만 노출한다.
      */}
      <div className="mt-5 flex justify-center gap-2">
        {post.author.id !== myId && isMarketBoard(post.boardSlug) && (
          <button
            disabled={busy}
            onClick={() => void toggleFavorite()}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-medium transition active:scale-[0.97] disabled:opacity-50 ${
              post.favorited
                ? 'bg-tomato-600 text-white'
                : 'bg-surface text-ink-700 shadow-card hover:bg-ink-50'
            }`}
          >
            <span key={String(post.favorited)} className="feedback-pop">
              {post.favorited ? '❤️' : '🤍'}
            </span>
            찜 {post.favoriteCount > 0 && <span className="tabular-nums">{post.favoriteCount}</span>}
          </button>
        )}
        {post.author.id !== myId && (
          <button
            disabled={busy}
            onClick={() => void toggleLike()}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-medium transition active:scale-[0.97] disabled:opacity-50 ${
              post.liked
                ? 'bg-tomato-600 text-white'
                : 'bg-surface text-ink-700 shadow-card hover:bg-ink-50'
            }`}
          >
            <span key={String(post.liked)} className="feedback-pop">
              {post.liked ? '👍' : '👍🏻'}
            </span>
            추천 {post.likeCount > 0 && <span className="tabular-nums">{post.likeCount}</span>}
          </button>
        )}
        {/* 공유는 본인 글 포함 누구나. 링크로 로그아웃 방문자에게 프리뷰가 열린다. */}
        <button
          onClick={() => void sharePost()}
          className="flex items-center gap-2 rounded-full bg-surface px-5 py-2.5 text-[14px] font-medium text-ink-700 shadow-card transition hover:bg-ink-50 active:scale-[0.97]"
        >
          <span>🔗</span>
          {shareCopied ? '복사됨!' : '공유'}
        </button>
      </div>

      {/*
        "N명이 찜함"은 작성자 포함 모두에게 카운트만 보여준다.
        **누가 찜했는지는 절대 노출하지 않는다**(절대 규칙 9). 작성자 본인에게도 목록은 없다.
      */}
      {isMarketBoard(post.boardSlug) && post.favoriteCount > 0 && (
        <p className="mt-3 text-center text-[12px] text-ink-400">
          {post.favoriteCount}명이 찜했어요
        </p>
      )}

      {/*
        판매자와 1:1 대화. 찜한 사람만 시작할 수 있다(D1) — 서버가 최종 판정하고,
        여기선 찜 안 했을 때 찜을 먼저 유도한다. 판매자 본인에게는 안 보인다(선메시지 경로 없음).
      */}
      {isMarketBoard(post.boardSlug) && post.tradeStatus !== 'sold' && post.author.id !== myId && (
        <div className="mt-5">
          <Button className="w-full" disabled={busy} onClick={() => void startChat()}>
            💬 판매자와 대화하기
          </Button>
          <p className="mt-2 text-center text-[12px] text-ink-400">
            대화를 시작하면 판매자에게 내 활동명이 보여요.
          </p>
        </div>
      )}

      {post.canEdit && (
        <div className="mt-4 flex justify-end gap-1">
          {/*
            끌올은 작성자만 한다. canEdit으로 판단하면 관리자에게도 버튼이 뜨는데,
            그러면 "오늘 끌올함"이라는 사실과 다른 문구를 보게 된다
            (실제로는 오늘 끌올해서가 아니라 남의 글이라서 못 하는 것).
          */}
          {post.tradeType && post.author.id === myId && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy || !post.canBump}
              onClick={() => void bump()}
            >
              {post.canBump ? '끌올' : '오늘 끌올함'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate(`/posts/${post.id}/edit`)}>
            수정
          </Button>
          {confirmDelete ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                취소
              </Button>
              <Button variant="danger" size="sm" disabled={busy} onClick={() => void remove()}>
                정말 삭제할까요?
              </Button>
            </>
          ) : (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
              삭제
            </Button>
          )}
        </div>
      )}

      <Notice>{error}</Notice>

      <CommentSection postId={post.id} onCountChange={() => void refreshCounts()} />
    </article>
  );
}
